import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { simulateBankDeposit } from '@services/api';
import { SaudiRiyalSymbol } from '@components/SaudiRiyalSymbol';

/**
 * In-app Bank Simulator: vIBAN + Amount + Deposit.
 * POSTs to Backend /api/webhooks/bank-callback so no separate bank-simulator server is required.
 */
export function BankSimulatorPage() {
  const { t } = useTranslation();
  const [virtualIban, setVirtualIban] = useState('');
  const [amount, setAmount] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<{ text: string; error: boolean } | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const viban = virtualIban.trim().replace(/\s/g, '');
    const numAmount = parseFloat(amount);
    if (!/^SA[0-9]{22}$/.test(viban)) {
      setMessage({ text: 'vIBAN must be SA + 22 digits (e.g. SA4480000000608010167519)', error: true });
      return;
    }
    if (!Number.isFinite(numAmount) || numAmount <= 0) {
      setMessage({ text: 'Amount must be a positive number', error: true });
      return;
    }
    setMessage(null);
    setLoading(true);
    try {
      const { data } = await simulateBankDeposit(viban, numAmount);
      if (data.alreadyProcessed) {
        setMessage({ text: 'Already processed (idempotent).', error: false });
      } else {
        setMessage({
          text: `Deposit recorded. ${data.referenceNumber ? `Reference: ${data.referenceNumber}` : data.message ?? ''}`.trim(),
          error: false,
        });
      }
    } catch (err) {
      const msg = (err as { message?: string })?.message ?? 'Request failed';
      setMessage({ text: msg, error: true });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen font-sans bg-surface-web p-6">
      <div className="max-w-md mx-auto">
        <h1 className="text-xl font-semibold text-primary mb-4">
          {t('bankSimulator.title', { defaultValue: 'Bank Simulator – vIBAN Deposit' })}
        </h1>
        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          <div>
            <label htmlFor="bank-sim-viban" className="block text-sm font-medium text-primary mb-1">
              {t('bankSimulator.viban', { defaultValue: 'vIBAN (SA + 22 digits)' })}
            </label>
            <input
              id="bank-sim-viban"
              type="text"
              value={virtualIban}
              onChange={(e) => setVirtualIban(e.target.value)}
              placeholder="SA4480000000608010167519"
              maxLength={24}
              className="w-full px-3 py-2 rounded-lg border border-primary/20 bg-surface-cards text-primary font-mono"
              dir="ltr"
            />
          </div>
          <div>
            <label htmlFor="bank-sim-amount" className="block text-sm font-medium text-primary mb-1">
              <span className="inline-flex items-center gap-1">
                {t('bankSimulator.amount', { defaultValue: 'Amount' })}
                <SaudiRiyalSymbol className="h-4 w-4 text-primary/80" />
              </span>
            </label>
            <input
              id="bank-sim-amount"
              type="number"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              min={0.01}
              step={0.01}
              className="w-full px-3 py-2 rounded-lg border border-primary/20 bg-surface-cards text-primary"
            />
          </div>
          <button
            type="submit"
            disabled={loading}
            className="px-4 py-3 rounded-xl bg-primary text-white font-medium disabled:opacity-60"
          >
            {loading ? t('common.loading', { defaultValue: '…' }) : t('bankSimulator.deposit', { defaultValue: 'Deposit' })}
          </button>
        </form>
        {message && (
          <div
            role="alert"
            className={`mt-4 p-3 rounded-lg text-sm ${message.error ? 'bg-surface-error-lighter text-surface-error-primary' : 'bg-secondary/10 text-secondary'}`}
          >
            {message.text}
          </div>
        )}
      </div>
    </div>
  );
}
